import { Chip, tooltipClasses } from "@mui/material";
import { GridColDef, GridRowId } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbProgress } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import { HotelClinicStatusItem, hotelClinicStatusItems } from "@/constants/hotelClinicStatuses";
import { HotelClinicStatus } from "@/models/hotel-clinic.model";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";

import { HotelClinicTaskResolved } from "../../types";
import { HotelClinicTaskStatusMilestonesModal } from "./HotelClinicTaskStatusMilestonesModal";

type DetailPassengerRow = {
    status: HotelClinicStatus;
    name: string;
    hotel: string;
    companions?: { name: string }[];
    _isPlaceholder?: true;
};

function TasksStatusColumn({ t }: { t: TFunction }): GridColDef {
    return {
        field: "status",
        headerName: t("status"),
        minWidth: 150,
        maxWidth: 210,
        flex: 0.5,
        filterable: false,
        valueGetter: (_val, row) => row.status,
        renderHeader: (params) => <TableHeader Component={TbProgress} title={params.colDef.headerName!} />,
        renderCell: ({ value, id, row }) =>
            (row as HotelClinicTaskResolved & { _isPlaceholder?: true })._isPlaceholder ? (
                <div className="w-full h-full" />
            ) : (
                <TasksDetailStatusCell
                    value={value as HotelClinicStatus}
                    id={id}
                    row={row as HotelClinicTaskResolved}
                />
            ),
        getApplyQuickFilterFn: (value: string) => {
            if (!value) return () => true;

            const searchText = value.toLowerCase();

            return (val, row) => {
                const r = row as DetailPassengerRow;
                if (r._isPlaceholder) return false;

                const matches = (target: string) =>
                    target?.toLowerCase().localeCompare(searchText, undefined, { sensitivity: "base" }) === 0 ||
                    target?.toLowerCase().includes(searchText);

                const matchesRowValue = matches(val?.toString());
                const matchesNameOrHotel = matches(r.name) || matches(r.hotel);
                const matchesCompanions = r.companions?.some((c) => matches(c.name)) ?? false;

                return matchesRowValue || matchesNameOrHotel || matchesCompanions;
            };
        },
    };
}

export function TasksDetailStatusCell({
    value,
    row,
}: {
    value: HotelClinicStatus;
    id: GridRowId;
    row: HotelClinicTaskResolved;
}) {
    const status = useMemo(() => hotelClinicStatusItems.find((item) => item.status === value), [value]);

    return (
        <HotelClinicTaskStatusMilestonesModal statusMilestones={row.statusMilestones} taskId={row.id}>
            <div className="w-full h-full pl-2 py-1 flex items-center">{status && <StatusChip {...status} />}</div>
        </HotelClinicTaskStatusMilestonesModal>
    );
}

function StatusChip(status: HotelClinicStatusItem) {
    const { t } = useTranslation();

    return (
        <NoMaxWidthTooltip
            arrow
            sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                    backgroundColor: "white",
                    border: "none",
                    padding: "0px !important",
                    fontSize: 12,
                    maxWidth: "200px !important",
                },
            }}
            title={<HotelStatusTooltip status={status} />}
        >
            <Chip
                sx={{
                    backgroundColor: status.bg,
                    color: status.text,
                    fontWeight: 500,
                    fontSize: "12px !important",
                }}
                icon={createElement(status.component(), { fontSize: 14, color: status.text })}
                label={t(status.label)}
            />
        </NoMaxWidthTooltip>
    );
}

export default TasksStatusColumn;
